import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import DashboardLayout from "../components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Video, Play, CheckCircle, Clock, Eye, Film, ArrowLeft } from "lucide-react";
import { API } from "../App";
import { useI18n } from "../lib/i18n";

const formatFileSize = (bytes) => {
  if (!bytes) return "";
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} Ko`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} Go`;
};

const PatientMeditationsPage = ({ user, logout }) => {
  const { t } = useI18n();
  const sessionToken = localStorage.getItem("session_token") || "";
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [playingId, setPlayingId] = useState(null);
  const [streamUrl, setStreamUrl] = useState(null);

  useEffect(() => {
    fetchAssignments();
  }, []);

  const fetchAssignments = async () => {
    try {
      const res = await axios.get(`${API}/meditations/my`, { withCredentials: true });
      setAssignments(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const markViewed = async (meditationId) => {
    try {
      await axios.post(`${API}/meditations/${meditationId}/viewed`, {}, { withCredentials: true });
      setAssignments(prev =>
        prev.map(a =>
          a.meditation_id === meditationId ? { ...a, viewed: true, viewed_at: new Date().toISOString() } : a
        )
      );
    } catch (e) {
      console.error(e);
    }
  };

  const handlePlay = async (meditationId) => {
    setPlayingId(meditationId);
    setStreamUrl(null);
    markViewed(meditationId);
    try {
      const res = await axios.get(`${API}/meditations/${meditationId}/stream?token=${sessionToken}`, { withCredentials: true });
      setStreamUrl(res.data.stream_url);
    } catch (e) {
      toast.error(t("meditations.streamError", "Erreur de lecture video"));
      setPlayingId(null);
    }
  };

  return (
    <DashboardLayout user={user} logout={logout}>
      <div className="space-y-8" data-testid="patient-meditations-page">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold">{t("meditations.patientTitle", "Mes Meditations")}</h1>
          <p className="text-muted-foreground text-sm sm:text-base mt-1">{t("meditations.patientSubtitle", "Videos de meditation guidee assignees par votre therapeute")}</p>
        </div>

        {playingId ? (
          <Card data-testid="video-player-card">
            <CardContent className="p-0">
              {(() => {
                const assignment = assignments.find(a => a.meditation_id === playingId);
                const med = assignment?.meditation;
                return (
                  <div>
                    <div className="bg-black rounded-t-lg overflow-hidden">
                      {streamUrl ? (
                        <video
                          controls
                          autoPlay
                          className="w-full max-h-[70vh]"
                          src={streamUrl}
                          data-testid="video-player"
                        >
                          {t("meditations.noSupport", "Votre navigateur ne supporte pas la lecture video.")}
                        </video>
                      ) : (
                        <div className="flex items-center justify-center h-64">
                          <div className="text-white text-center">
                            <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                            <p>{t("meditations.loadingVideo", "Chargement de la video...")}</p>
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="p-6 space-y-3">
                      <h2 className="font-heading text-xl font-semibold">{med?.title}</h2>
                      {med?.description && <p className="text-muted-foreground">{med.description}</p>}
                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        {med?.size && <span>{formatFileSize(med.size)}</span>}
                        {med?.language && <span className="uppercase">{med.language}</span>}
                        {assignment?.viewed && (
                          <span className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400">
                            <CheckCircle className="w-3.5 h-3.5" /> {t("meditations.viewed", "Vue")}
                          </span>
                        )}
                      </div>
                      <Button variant="outline" onClick={() => { setPlayingId(null); setStreamUrl(null); }} className="gap-2" data-testid="back-to-list-btn">
                        <ArrowLeft className="w-4 h-4" />
                        {t("meditations.backToList", "Retour a la liste")}
                      </Button>
                    </div>
                  </div>
                );
              })()}
            </CardContent>
          </Card>
        ) : (
          <div>
            {loading ? (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {[1, 2, 3].map(i => (
                  <Card key={i} className="animate-pulse">
                    <CardContent className="p-6"><div className="h-32 bg-muted rounded-lg mb-4" /><div className="h-4 bg-muted rounded w-3/4 mb-2" /><div className="h-3 bg-muted rounded w-1/2" /></CardContent>
                  </Card>
                ))}
              </div>
            ) : assignments.length > 0 ? (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {assignments.map(assignment => {
                  const med = assignment.meditation;
                  if (!med) return null;
                  return (
                    <Card
                      key={assignment.assignment_id || assignment.meditation_id}
                      className="card-hover cursor-pointer group overflow-hidden"
                      onClick={() => handlePlay(assignment.meditation_id)}
                      data-testid={`patient-meditation-${assignment.meditation_id}`}
                    >
                      <div className="relative h-40 bg-gradient-to-br from-primary/10 to-primary/5 flex items-center justify-center">
                        <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center group-hover:bg-primary/30 transition-colors">
                          <Play className="w-8 h-8 text-primary ml-1" />
                        </div>
                        {assignment.viewed && (
                          <div className="absolute top-3 right-3 flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-100 dark:bg-emerald-900/50 text-emerald-700 dark:text-emerald-300 text-xs">
                            <Eye className="w-3 h-3" />
                            {t("meditations.viewed", "Vue")}
                          </div>
                        )}
                        {!assignment.viewed && (
                          <div className="absolute top-3 right-3 flex items-center gap-1 px-2 py-1 rounded-full bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-300 text-xs">
                            <Clock className="w-3 h-3" />
                            {t("meditations.new", "Nouveau")}
                          </div>
                        )}
                      </div>
                      <CardContent className="p-4">
                        <h3 className="font-heading font-semibold mb-1 truncate">{med.title}</h3>
                        {med.description && <p className="text-sm text-muted-foreground line-clamp-2 mb-2">{med.description}</p>}
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          {med.size && <span>{formatFileSize(med.size)}</span>}
                          {med.language && <span className="uppercase">{med.language}</span>}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            ) : (
              <Card>
                <CardContent className="py-16 text-center">
                  <Film className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                  <p className="text-lg font-medium text-muted-foreground">{t("meditations.noAssignments", "Aucune meditation assignee")}</p>
                  <p className="text-sm text-muted-foreground text-sm sm:text-base mt-1">{t("meditations.noAssignmentsHint", "Votre therapeute vous assignera des videos de meditation")}</p>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default PatientMeditationsPage;
